import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import BrandMark from "../components/BrandMark";
import { api, formatApiError } from "../lib/api";

export default function Signup() {
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [orgName, setOrgName] = useState("");
  const [adminName, setAdminName] = useState("");
  const [email, setEmail] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/billing/config")
      .then((r) => { if (!cancelled) setPlan(r.data); })
      .catch(() => {
        if (!cancelled) setError("Billing service is unavailable. Please try again later.");
      });
    return () => { cancelled = true; };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!agreed) {
      setError("Please agree to the terms before continuing.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/billing/checkout", {
        email: email.trim().toLowerCase(),
        organization_name: orgName.trim(),
        admin_name: adminName.trim(),
        origin_url: window.location.origin,
      });
      // Redirect to Stripe Checkout
      window.location.href = r.data.url;
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || "Could not start checkout. Please try again.");
      toast.error("Checkout failed");
    } finally {
      setSubmitting(false);
    }
  };

  const priceLabel = plan
    ? `$${(plan.plan.amount_cents / 100).toFixed(0)}/${plan.plan.interval}`
    : "$29/month";

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Top bar */}
      <header className="bg-[#1A2332] text-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="signup-brand-link">
            <BrandMark size={32} className="rounded-md" />
            <span className="font-display font-bold">ezItemizer</span>
          </Link>
          <Link
            to="/login"
            className="text-xs font-bold uppercase tracking-widest text-white/70 hover:text-white"
          >
            Already have an account? Sign in
          </Link>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12 lg:py-16 grid lg:grid-cols-5 gap-10">
        {/* Plan summary */}
        <aside className="lg:col-span-2 space-y-6" data-testid="signup-plan-summary">
          <div className="bg-white border border-zinc-300">
            <div className="bg-[#1A2332] text-white px-6 py-4">
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF9800]">
                Subscription
              </div>
              <div className="font-display font-black text-2xl mt-1">ezItemizer Basic</div>
            </div>
            <div className="px-6 py-5 border-b border-zinc-200">
              <div className="flex items-end gap-2">
                <div className="font-display font-black text-5xl text-[#0F172A]">
                  {priceLabel.split("/")[0]}
                </div>
                <div className="text-zinc-500 font-mono text-xs uppercase tracking-widest pb-2">
                  /{plan?.plan.interval || "month"}
                </div>
              </div>
              <div className="mt-3 inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-3 py-1.5">
                <CheckCircle size={14} weight="fill" className="text-emerald-600" />
                <span className="text-xs font-bold uppercase tracking-widest text-emerald-700">
                  {plan?.plan.trial_days || 14}-day free trial
                </span>
              </div>
            </div>
            <ul className="px-6 py-5 space-y-3 text-sm text-zinc-700">
              {[
                "Unlimited parts & SKUs",
                "Real-time stock counts + audit trail",
                "Camera barcode + QR scanning (PWA)",
                "Low-stock push + email alerts",
                "Role-based access (Admin / Viewer)",
                "CSV & PDF inventory reports",
                "2 users included (1 admin + 1 viewer)",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <CheckCircle size={16} weight="fill" className="text-[#FF9800] flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="px-6 py-4 bg-zinc-50 border-t border-zinc-200 flex items-start gap-2 text-[11px] text-zinc-500 leading-relaxed">
              <ShieldCheck size={14} weight="duotone" className="text-zinc-500 flex-shrink-0 mt-0.5" />
              <span>
                Card required to start trial. No charge today — billed {priceLabel} after the
                trial ends. Cancel anytime from your billing page.
              </span>
            </div>
          </div>
        </aside>

        {/* Signup form */}
        <main className="lg:col-span-3">
          <div className="bg-white border border-zinc-300">
            <div className="px-6 py-5 sm:px-8 sm:py-7 border-b border-zinc-200">
              <h1 className="font-display font-black text-2xl sm:text-3xl text-[#0F172A]">
                Start your free trial
              </h1>
              <p className="mt-2 text-sm text-zinc-600">
                Tell us about your shop. We'll create your account after Stripe verifies your card.
              </p>
            </div>

            <form onSubmit={onSubmit} className="px-6 py-6 sm:px-8 sm:py-8 space-y-5" data-testid="signup-form">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  Company / Organization name
                </label>
                <input
                  type="text"
                  required
                  minLength={2}
                  maxLength={120}
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="e.g. Sunset Trailers Manufacturing"
                  data-testid="signup-org-input"
                  className="mt-1.5 w-full bg-white border border-zinc-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9800] focus:border-[#FF9800]"
                  disabled={submitting}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Your name
                  </label>
                  <input
                    type="text"
                    required
                    minLength={1}
                    value={adminName}
                    onChange={(e) => setAdminName(e.target.value)}
                    placeholder="Pat Owner"
                    data-testid="signup-admin-name-input"
                    className="mt-1.5 w-full bg-white border border-zinc-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9800] focus:border-[#FF9800]"
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                    Work email
                  </label>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    data-testid="signup-email-input"
                    className="mt-1.5 w-full bg-white border border-zinc-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9800] focus:border-[#FF9800]"
                    disabled={submitting}
                  />
                </div>
              </div>

              <label className="flex items-start gap-2.5 text-sm text-zinc-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 accent-[#FF9800]"
                  data-testid="signup-agree-checkbox"
                />
                <span>
                  I understand the trial is free for {plan?.plan.trial_days || 14} days, and after
                  that my card will be billed {priceLabel} until I cancel.
                </span>
              </label>

              {error && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                  <WarningCircle size={16} weight="fill" className="flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !agreed}
                data-testid="signup-submit-button"
                className="w-full bg-[#FF9800] hover:bg-[#E68900] text-[#1A2332] font-bold py-3.5 px-4 flex items-center justify-center gap-2 transition-colors active:scale-[0.99] disabled:opacity-60"
              >
                {submitting ? "Redirecting to secure checkout…" : (
                  <>
                    Continue to Stripe checkout
                    <ArrowRight size={16} weight="bold" />
                  </>
                )}
              </button>

              <div className="flex items-center justify-center gap-1.5 text-[11px] text-zinc-500">
                <ShieldCheck size={12} weight="duotone" />
                <span>Secure payment powered by Stripe</span>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}
